import Form from "../form/Form";
import { notice } from "../utils/notice";

export default class SingleProductForm {
    /**
     * @type {HTMLFormElement}
     */
    formElement;

    /**
     * @type {Form}
     */
    formInstance;

    /**
     * @type {HTMLElement}
     */
    priceContainer;

    isProcessToProduct = false;

    variations;
    colors;

    _variation;

    get variation() {
        return this._variation;
    }

    set variation(value) {
        this._variation = value ? {...value} : undefined;
        this.onVariationChange();
    }

    _color;

    get color() {
        return this._color;
    }

    set color(value) {
        if (value === this._color) {
            return;
        }
        this._color = value;
        this.onColorChange();
    }

    /**
     * 
     * @param {HTMLFormElement} form 
     * @returns 
     */
    constructor(form) {
        if (!form) {
            return;
        }

        this.formElement = form;
        this.formInstance = new Form(this.formElement);
        this.priceContainer = this.formElement.querySelector('.product-page__info-payment-price');
        this.btnSubmit = this.formElement.querySelector('.product-page__info-payment-btn');

        this.findVariationsData();
        this.findColorsData();
        this.addListeners();
        this.initColor();
        this.initVariation();
    }   

    /**
     * Find variations data and remove script element
     */
    findVariationsData() {
        if (!this.formElement) {
            return;
        }

        const scriptEl = this.formElement.querySelector('[data-product-variations]');

        if (!scriptEl) {
            return;
        }
        try {
            this.variations = JSON.parse(scriptEl.textContent);
            scriptEl.remove();
        } catch (error) {
            console.warn(error);
        }
    }

    findColorsData() {
        if (!this.formElement) {
            return;
        }

        const scriptEl = this.formElement.querySelector('[data-colors-data]');
        if (!scriptEl) {
            return;
        }
        try {
            this.colors = JSON.parse(scriptEl.textContent);
            scriptEl.remove();
        } catch (error) {
            console.warn(error);
        }
    }

    /**
     * Add listeners to formInstance
     */
    addListeners() {
        if (!this.formInstance) {
            return;
        }

        this.formInstance.on('change', (data) => {
            this.onFomrChange(data);
        });

        this.formInstance.on('submit', (data) => {
            this.onFormSubmit(data);
        });
    }

    initVariation() {
        if (!this.formInstance) {
            return;
        }

        this.onFomrChange(this.formInstance.collectValues());
    }

    initColor() {
        if (!this.formInstance) {
            return;
        }

        this.checkColorChange(this.formInstance.collectValues(), false);
    }

    /**
     * formInstance's change callback
     * 
     * @param {Object} data - Fields data from formInstance
     */
    onFomrChange(data) {
        const attributes = this.getAttributes(data);
        this.findVariation(attributes);

        this.checkColorChange(data);
    }

    /**
     * formInstance's submit callback
     * 
     * @param {Object} data - Fields data from formInstance
     */
    onFormSubmit(data) {
        const attributes = !this.variation ? {} : this.getAttributes(data);

        const fields = {
            attrs: attributes,
            nonce: data._nonce,
            referer: data._wp_http_referer,
            
            product_id: Number(this.formElement.dataset.productForm),
        }

        if (this.variation) {
            fields.variation_id = this.variation.variation_id;
        }

        const formData = new FormData();

        formData.append('action', 'add_to_cart');
        formData.append('data', JSON.stringify(fields));

        this.formElement.classList.add('loading');
        this.btnSubmit.classList.add('processing');


        fetch(backendData.ajaxUrl, {
            method: 'POST',
            body: formData,
        })
            .then(resp => resp.json())
            .then(response => {
                const {
                    data,
                    success,
                } = response;
                
                if (success) {
                    this.onAddedSuccess(data.cart_count, data.message);
                } else {
                    this.onAddedError(data.message);
                }
            })
            .catch(error => {
                this.onAddedError(error);
            })
            .finally(() => {
                this.formElement.classList.remove('loading');
                this.btnSubmit.classList.remove('processing');
            });
    }

    onAddedError(error) {
        notice(error, 'error');
    }

    onAddedSuccess(cartCount = 1, message) {
        window.cartCounter.count = cartCount;
        notice(message);
    }

    /**
     * Get only attributes from formInstance's
     * fields data
     * 
     * @param {Object} data - Fields data from formInstance
     * @returns {Object}    - Formatted data for attributes
     */
    getAttributes(data) {
        const output = {};

        Object.keys(data).forEach(key => {
            if (key.includes('pa_')) {
                output[`attribute_${key}`] = data[key];
            } else if (key.includes('ca_')) {
                output[`attribute_${key.replace('ca_', '')}`] = data[key];
            }
        });

        return output;
    }

    /**
     * Find variation based on chosen attributes
     * 
     * @param {Object} attrs 
     */
    findVariation(attrs) {
        if (!this.variations) {
            return;
        }

        const variation = this.variations.find((variation) => {

            const results = [];

            Object.keys(variation.attributes).forEach(key => {
                results.push(
                    attrs[key] === variation.attributes[key] 
                    || variation.attributes[key] === ''
                );
            });

            return results.every(r => r === true);
        });
        
        this.variation = variation;
    }

    /**
     * Fires when variation changes
     */
    onVariationChange() {
        if (!this.variation) {
            return;
        }

        this.changePrice();
    }

    /**
     * Change price HTML
     */
    changePrice() {
        if (!this.priceContainer || !this.variation || !this.variation.price_html) {
            return;
        }

        this.priceContainer.innerHTML = this.variation.price_html;
    }
    
    /**
     * Fires when color changes
     */
    onColorChange() {
        if (!this.colors || this.isProcessToProduct) {
            return;
        }
        const colorItem = this.colors.find(c => c.term.split('-')[0] === this.color);
        
        if (!colorItem) {
            return;
        }

        this.isProcessToProduct = true;

        this.formInstance.reset();
       
        this.formElement.classList.add('loading');
     
        window.open(colorItem.link, '_self');
    }

    /**
     * Get color value from formInstance fields data
     * 
     * @param {Object} data   - formInstance fields data
     * @param {boolean} force - If we need to trigger change event
     */
    checkColorChange(data, force = true) {
        const color = Object.keys(data).find(key => key.includes('color'));

        if (!color) {
            return;
        }

        if (force) {
            this.color = data[color];
        } else {
            this._color = data[color];
        }
    }
}
